import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useLoader from '../../../../hooks/useLoader'
import useForm from '../../../../hooks/useForm'
import Form from '../../../../components/forms/Form'
import { brandFields } from '../../../../constants/forms/almoxarifado/brandFields'
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../../utils/objectUtils'
import useBaseService from '../../../../hooks/services/useBaseService'
import { entities } from '../../../../constants/entities'
import PageHeader from '../../../../components/PageHeader'
import SimpleBody from '../../../../components/forms/SimpleBody'
import SectionHeader from '../../../../components/forms/SectionHeader'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import MainLayout from '../../../../layouts/MainLayout'
import useQueryWithLoader from '../../../../hooks/useQueryWithLoader'

const EditBrandsPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)
  const { showLoader, hideLoader } = useLoader()
  const queryClient = useQueryClient()

  const [brandSection] = brandFields

  const { formData, handleChange, resetForm, setFormData } = useForm(
    setDefaultFieldValues([brandSection]),
    [brandSection]
  )

  // Buscar informações do produto
  const { data: brand, isLoading: isLoadingBrand } = useQueryWithLoader({
    queryKey: ['brand', id],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(entities.brands.getById(id))
      return response.result
    },
    enabled: !!id,
  })

  useEffect(() => {
    if (brand) {
      setFormData({
        brand: {
          ...brand,
        },
      })
    }
  }, [brand, setFormData])

  const updateBrand = useMutation({
    mutationFn: async (data) => {
      showLoader()
      const response = await baseServicePut(entities.brands.update(id), data)
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader()
        resetForm()
        navigate('/almoxarifado/produtos/marcas')
        queryClient.invalidateQueries({
          queryKey: ['almox_brands'],
        })
        queryClient.invalidateQueries({
          queryKey: ['brandOptions'],
        })
      }
    },
    onError: (error) => {
      console.log('onError ', error)
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })
  const handleSubmit = async () => {
    try {
      const cleanedProduct = cleanPayload(formData)
      const payload = {
        brand: {
          ...cleanedProduct.brand,
        },
      }
      return await updateBrand.mutateAsync(payload)
    } catch (error) {
      console.error('Erro ao enviar marca:', {
        status: error.status,
        message: error.message,
      })
    }
  }

  const handleBack = () => {
    navigate(`/almoxarifado/produtos/marcas`)
  }

  const isPageLoading = isLoadingBrand

  useEffect(() => {
    if (isPageLoading) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isPageLoading, showLoader, hideLoader])

  return (
    <MainLayout>
      <PageHeader
        title="Editar Marca"
        showBackButton={true}
        backUrl="/almoxarifado/produtos/marcas"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Atualizar Marca"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
          isLoading={isPageLoading || updateBrand.isPending}>
          {() => {
            const renderedSections = new Set()

            return brandFields.map((section, index) => {
              const sectionType = section.section.split(' - ')[0]
              const showHeader = !renderedSections.has(sectionType)

              if (showHeader) {
                renderedSections.add(sectionType)
              }

              return (
                <div key={section.section + ' ' + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              )
            })
          }}
        </Form>
      </div>
    </MainLayout>
  )
}

export default EditBrandsPage
